import { CustomConfig } from '../config/models/customConfig';
import { IStorageExtension, StorageDataModel } from '../extensions';
import { ILanguageProvider } from '../language';
import { Logger } from '../logging';
import { ITheme, IThemeBackend, IThemeManager } from './interfaces';
import { ThemeProperties } from './themeProperties';
import { Mat3CompactTheme } from './themes/mat3CompactTheme';
import { ThemeVariables } from './themeVariables';
import { ThemeVariablesManager } from './themeVariablesManager';

/**
 * Handles the themeing funtionallity.
 * 
 * @export
 * @class ThemeManager
 */
export class ThemeManager implements IThemeManager {
    public readonly availableThemes: ITheme[];
    private readonly className = 'ThemeManager';
    private readonly documentAttributeThemeKey = 'data-wd-theme';
    private readonly defaultThemeId = 'material.three.compact';
    private readonly componentStorageName = 'com.windream.profile.settings';
    private readonly themeStorageId = 'currentTheme';
    private backend: IThemeBackend;
    private logger: Logger;
    private storageExtension: IStorageExtension;
    private customConfig?: CustomConfig;
    private documentRef: Document;
    private languageProvider: ILanguageProvider;
    private themeVariablesManager: ThemeVariablesManager;


    /**
     * Creates an instance of ThemeManager.
     *
     * @param {Document} documentRef Reference to the document object.
     * @param {ILanguageProvider} languageProvider The language provider.
     * @param {IStorageExtension} storageExtension The storage extension.
     * @param {Logger} logger The logger.
     * @param {IThemeBackend} backend The theme backend.
     * @param {IViewManager} viewManager The view manager.
     * @memberof ThemeManager
     */
    public constructor(documentRef: Document, languageProvider: ILanguageProvider, storageExtension: IStorageExtension, logger: Logger,
        backend: IThemeBackend) {
        this.documentRef = documentRef;
        this.languageProvider = languageProvider;
        this.logger = logger;
        this.storageExtension = storageExtension;
        this.backend = backend;
        this.availableThemes = new Array();
        this.themeVariablesManager = new ThemeVariablesManager(documentRef);

        // Add all supported themes
        this.registerTheme(new Mat3CompactTheme(documentRef, this.themeVariablesManager, this.languageProvider));
    }


    /**
     * Uses the default values of the current theme.
     * This is the same as disable all special styles like view styles, etc.
     *
     * @memberof ThemeManager
     */
    public useCurrentThemeDefaultValues(): void {
        this.updateCurrentThemeProperties(undefined);
    }

    /**
     * Updates the current theme properties.
     *
     * @param {ThemeProperties} [properties] The properties to apply.
     * @memberof ThemeManager
     */
    public updateCurrentThemeProperties(properties?: ThemeProperties): void {
        const currentTheme = this.currentTheme();
        if (currentTheme) {
            currentTheme.updateThemeProperties(properties);

            currentTheme.apply();
            this.applyTheme();
        }
    }

    /**
     * Applies the current theme.
     *
     * @private
     * @memberof ThemeManager
     */
    private applyTheme(): void {
        const theme = this.getTheme();
        this.applyCustomConfig(theme);
    }

    /**
     * Loads and applies the last used theme.
     *
     * @memberof ThemeManager
     */
    public loadTheme() {
        this.loadThemeFromStorage()
            .then((id) => {
                this.changeTheme(id);
            })
            .catch((err) => this.logger.error(
                this.className,
                'loadTheme',
                'Failed to load theme from browser storage',
                err));
    }

    /**
     * Change the application theme by a theme ID.
     * 
     * The current theme will not be changed if the specified ID is invalid.
     *
     * @param {string} id ID of the theme to change to.
     * @memberof ThemeManager
     */
    public changeTheme(id: string) {
        const newTheme = this.availableThemes.find((theme: ITheme) => theme.id === id);
        if (newTheme) {
            this.documentRef.documentElement.setAttribute(this.documentAttributeThemeKey, newTheme.id);
            this.saveThemeToStorage(newTheme.id)
                .then(() => {
                    this.themeVariablesManager.resetVariables();

                    this.backend.setActiveTheme(newTheme.id);
                    newTheme.apply();
                    this.applyTheme();
                })
                .catch((err) => this.logger.error(
                    this.className,
                    'changeTheme',
                    'Failed to save theme to browser storage',
                    err));
        } else {
            this.logger.error(
                this.className,
                'changeTheme',
                `No registered theme found with ID: ${id}`);
        }
    }

    /**
     * Get the currently active theme.
     *
     * @returns {Theme} Theme object that is active.
     * @memberof ThemeManager
     */
    public currentTheme(): ITheme {
        return this.availableThemes.find(
            (theme) => theme.id === this.backend.getActiveTheme()
        ) || this.availableThemes[0];
    }

    /**
     * Sets the current custom configuration.
     * Will apply the given themeing by applying styles to the DOM.
     * 
     * @param {CustomConfig} customConfig Custom configuraiton to set.
     * @memberof ThemeManager
     */
    public setCustomConfig(customConfig: CustomConfig): void {
        this.customConfig = customConfig;
    }

    /**
     * Returns the currently used theme.
     *
     * @returns {CustomConfig}
     * @memberof ThemeManager
     */
    // eslint-disable-next-line complexity
    public getTheme(): CustomConfig {
        const themeConfig = this.currentTheme().getComputedCustomConfig(this.getDefaultConfig());
        if (!this.customConfig) {
            return themeConfig;
        }

        if (typeof this.customConfig.browserTitle === 'string') {
            themeConfig.browserTitle = this.customConfig.browserTitle;
        }
        if (typeof this.customConfig.title === 'string') {
            themeConfig.title = this.customConfig.title;
        }
        if (typeof this.customConfig.subTitle === 'string') {
            themeConfig.subTitle = this.customConfig.subTitle;
        }
        if (this.customConfig.navigationBackgroundColor !== '' && (typeof (this.customConfig.navigationBackgroundColor) !== 'undefined')) {
            themeConfig.navigationBackgroundColor = this.customConfig.navigationBackgroundColor;
        }
        if (this.customConfig.titleBackgroundColor !== '' && (typeof (this.customConfig.titleBackgroundColor) !== 'undefined')) {
            themeConfig.titleBackgroundColor = this.customConfig.titleBackgroundColor;
        }
        if (this.customConfig.titleTextColor !== '' && (typeof (this.customConfig.titleTextColor) !== 'undefined')) {
            themeConfig.titleTextColor = this.customConfig.titleTextColor;
        }
        if (this.customConfig.navigationTextColor !== '' && (typeof (this.customConfig.navigationTextColor) !== 'undefined')) {
            themeConfig.navigationTextColor = this.customConfig.navigationTextColor;
        }
        if (this.customConfig.iconPath !== '' && (typeof (this.customConfig.iconPath) !== 'undefined')) {
            themeConfig.iconPath = this.customConfig.iconPath;
        }
        if (this.customConfig.customCssFilePath !== '' && (typeof (this.customConfig.customCssFilePath) !== 'undefined')) {
            themeConfig.customCssFilePath = this.customConfig.customCssFilePath;
        }
        if (this.customConfig.headerBackgroundColor !== '' && (typeof (this.customConfig.headerBackgroundColor) !== 'undefined')) {
            themeConfig.headerBackgroundColor = this.customConfig.headerBackgroundColor;
        }
        if (this.customConfig.headerTextColor !== '' && (typeof (this.customConfig.headerTextColor) !== 'undefined')) {
            themeConfig.headerTextColor = this.customConfig.headerTextColor;
        }
        if (this.customConfig.activeViewBackgroundColor !== '' && (typeof (this.customConfig.activeViewBackgroundColor) !== 'undefined')) {
            themeConfig.activeViewBackgroundColor = this.customConfig.activeViewBackgroundColor;
        }
        if (this.customConfig.activeViewIconColor !== '' && (typeof (this.customConfig.activeViewIconColor) !== 'undefined')) {
            themeConfig.activeViewIconColor = this.customConfig.activeViewIconColor;
        }
        if (this.customConfig.activeViewColor !== '' && (typeof (this.customConfig.activeViewColor) !== 'undefined')) {
            themeConfig.activeViewColor = this.customConfig.activeViewColor;
        }
        if (this.customConfig.headerIconHoverColor !== '' && (typeof (this.customConfig.headerIconHoverColor) !== 'undefined')) {
            themeConfig.headerIconHoverColor = this.customConfig.headerIconHoverColor;
        }
        if (this.customConfig.navigationHoverColor !== '' && (typeof (this.customConfig.navigationHoverColor) !== 'undefined')) {
            themeConfig.navigationHoverColor = this.customConfig.navigationHoverColor;
        }
        if (this.customConfig.navigationHoverBackgroundColor !== '' && (typeof (this.customConfig.navigationHoverBackgroundColor) !== 'undefined')) {
            themeConfig.navigationHoverBackgroundColor = this.customConfig.navigationHoverBackgroundColor;
        }
        return themeConfig;
    }

    /**
     * Register all themes in the specified array.
     *
     * @private
     * @param {ITheme[]} [themes] Theme instances to register.
     * @memberof ThemeManager
     */
    public registerThemes(themes?: ITheme[]): void {
        if (themes) {
            themes.forEach((theme) => {
                this.registerTheme(theme);
            });
        }
    }

    /**
     * Applies the theme based on the given custom config.
     * 
     * @param {CustomConfig} customConfig Configuration to apply. 
     * @memberof ThemeManager
     */
    public applyCustomConfig(customConfig: CustomConfig): void {
        if (typeof customConfig.browserTitle === 'string') {
            this.documentRef.title = customConfig.browserTitle;
        }

        // Load external styles
        if (customConfig.customCssFilePath) {
            const customCssLinkElement = this.documentRef.createElement('link');
            customCssLinkElement.setAttribute('rel', 'stylesheet');
            customCssLinkElement.setAttribute('type', 'text/css');
            customCssLinkElement.setAttribute('href', customConfig.customCssFilePath);
            this.documentRef.head.appendChild(customCssLinkElement);
        }

        const appleMobileWebAppTitleElement = this.documentRef.querySelector('meta[name=\'apple-mobile-web-app-title\']');
        if (appleMobileWebAppTitleElement && typeof customConfig.browserTitle === 'string') {
            appleMobileWebAppTitleElement.setAttribute('content', customConfig.browserTitle);
        }

        // Apply theme variables
        this.applyThemeVariables(customConfig);
    }

    /**
     * Register a new theme with the theme manager.
     *
     * A theme must be registered before it can be used.
     * 
     * @private
     * @param {ITheme} theme Theme to register.
     * @memberof ThemeManager
     */
    private registerTheme(theme: ITheme) {
        this.availableThemes.push(theme);
    }

    /**
     * Load the theme ID from browser storage, that was selected by the user.
     * 
     * If the user never changed their theme, then the default one will be used.
     *
     * @private
     * @returns A promise containing the ID of the theme that was retrieved from browser storage.
     * @memberof ThemeManager
     */
    private async loadThemeFromStorage() {
        let currentThemeId: string = this.defaultThemeId;

        try {
            currentThemeId = await this.storageExtension.load(this.componentStorageName, this.themeStorageId);
        } catch (error) {
            this.logger.info(this.className, 'loadThemeFromStorage', `No theme set. Falling back to default: ${currentThemeId}`);
        }

        return currentThemeId;
    }

    /**
     * Save a theme ID to the browser storage.
     *
     * @private
     * @param {string} id ID of the theme to save.
     * @returns Promise of the async save operation.
     * @memberof ThemeManager
     */
    private async saveThemeToStorage(id: string) {
        const storageDataModel = new StorageDataModel(this.themeStorageId, id);
        return this.storageExtension.save(this.componentStorageName, storageDataModel);
    }

    /**
     * Applies the theme variables based on the given custom config.
     *
     * @private
     * @param {CustomConfig} customConfig The custom config to apply.
     * @memberof ThemeManager
     */
    private applyThemeVariables(customConfig: CustomConfig): void {
        if (customConfig.headerBackgroundColor) {
            this.themeVariablesManager.setVariableValue(ThemeVariables.appBarBackgroundColor, customConfig.headerBackgroundColor);
        }
        if (customConfig.headerTextColor) {
            this.themeVariablesManager.setVariableValue(ThemeVariables.appBarColor, customConfig.headerTextColor);
        }
        if (customConfig.titleBackgroundColor) {
            this.themeVariablesManager.setVariableValue(ThemeVariables.navigationHeaderBackgroundColor, customConfig.titleBackgroundColor);
        }
        if (customConfig.titleTextColor) {
            this.themeVariablesManager.setVariableValue(ThemeVariables.navigationHeaderColor, customConfig.titleTextColor);
        }
        if (customConfig.navigationHoverBackgroundColor) {
            this.themeVariablesManager.setVariableValue(ThemeVariables.hover_navigationItemBackgroundColor, customConfig.navigationHoverBackgroundColor);
        }
        if (customConfig.navigationHoverColor) {
            this.themeVariablesManager.setVariableValue(ThemeVariables.hover_navigationItemColor, customConfig.navigationHoverColor);
        }
        if (customConfig.navigationBackgroundColor) {
            this.themeVariablesManager.setVariableValue(ThemeVariables.navigationBackgroundColor, customConfig.navigationBackgroundColor);
        }
        if (customConfig.navigationTextColor) {
            this.themeVariablesManager.setVariableValue(ThemeVariables.navigationColor, customConfig.navigationTextColor);
            this.themeVariablesManager.setVariableValue(ThemeVariables.navigationIconColor, customConfig.navigationTextColor);
        }
        if (customConfig.activeViewBackgroundColor) {
            this.themeVariablesManager.setVariableValue(ThemeVariables.active_navigationItemBackgroundColor, customConfig.activeViewBackgroundColor);
        }
        if (customConfig.activeViewColor) {
            this.themeVariablesManager.setVariableValue(ThemeVariables.active_navigationItemColor, customConfig.activeViewColor);
        }
        if (customConfig.activeViewIconColor) {
            this.themeVariablesManager.setVariableValue(ThemeVariables.active_navigationItemIconColor, customConfig.activeViewIconColor);
        }
        if (customConfig.iconPath) {
            const navigationHeaderIcon = this.documentRef.getElementById('wd-navigation-panel-icon') as HTMLImageElement;
            if (navigationHeaderIcon) {
                // Const iconPath = getComputedStyle(this.documentRef.documentElement).getPropertyValue(`--${ThemeVariables.iconPath}`).trim().replace(/['"]/g, '');
                navigationHeaderIcon.src = customConfig.iconPath;
            }
        }
    }

    /**
     * Returns the default config.
     *
     * @private
     * @returns {CustomConfig}
     * @memberof ThemeManager
     */
    public getDefaultConfig(): CustomConfig {
        const defaultConfig = new CustomConfig();
        defaultConfig.activeViewBackgroundColor = '#b2b2b2';
        defaultConfig.activeViewIconColor = '#024c84';
        defaultConfig.browserTitle = this.languageProvider.get('framework.header.headerTitle');
        defaultConfig.customCssFilePath = '';
        defaultConfig.headerBackgroundColor = 'transparent';
        defaultConfig.headerIconHoverColor = '#d1d1d1';
        defaultConfig.headerTextColor = '#494949';
        defaultConfig.iconPath = './framework/assets/img/productIcon.svg';
        defaultConfig.navigationBackgroundColor = '#e7e7e7';
        defaultConfig.navigationHoverColor = '#d1d1d1';
        defaultConfig.navigationTextColor = '#494949';
        defaultConfig.subTitle = this.languageProvider.get('framework.header.subTitle');
        defaultConfig.title = this.languageProvider.get('framework.header.title');
        defaultConfig.titleBackgroundColor = '#e7e7e7';
        defaultConfig.titleTextColor = '#494949';
        return defaultConfig;
    }

}